import { useState } from 'react'
import { Flag, Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui/overlays/modal'
import { api } from '@/lib/api'
import { useUser } from '@clerk/clerk-react'

const REPORT_REASONS = [
  { value: 'csam', label: 'Child Sexual Abuse Material (CSAM)' },
  { value: 'non_consensual', label: 'Non-consensual intimate imagery' },
  { value: 'illegal_content', label: 'Other illegal content' },
  { value: 'other', label: 'Other violation' },
] as const

type ReportReason = typeof REPORT_REASONS[number]['value']

interface ReportContentModalProps {
  isOpen: boolean
  onClose: () => void
  generationId: string
}

export function ReportContentModal({ isOpen, onClose, generationId }: ReportContentModalProps) {
  const { user } = useUser()
  const [reason, setReason] = useState<ReportReason | ''>('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async () => {
    if (!reason || !user?.id) return

    setIsSubmitting(true)
    try {
      await api.post('/reports', {
        user_id: user.id,
        generation_id: generationId,
        reason,
        description: description.trim() || undefined,
      })
      setSubmitted(true)
    } catch {
      // Silently handle — the report may still have been saved
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    setReason('')
    setDescription('')
    setSubmitted(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Report Content">
      {submitted ? (
        <div className="text-center py-4">
          <div className="inline-flex items-center justify-center p-3 rounded-full bg-green-500/10 mb-3">
            <Flag className="size-6 text-green-400" />
          </div>
          <p className="text-sm text-white font-medium mb-1">Report Submitted</p>
          <p className="text-xs text-[#888] mb-4">
            Thank you for helping keep our platform safe. We will review this content shortly.
          </p>
          <button
            onClick={handleClose}
            className="px-4 py-2 rounded-lg bg-white/[0.05] text-sm text-white hover:bg-white/[0.1] transition-colors"
          >
            Close
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-xs text-[#888]">
            Report content that may be illegal or violates our policies. You can also
            email <a href="mailto:hello@pixelmaker.ai" className="text-violet-400 hover:underline">hello@pixelmaker.ai</a>.
          </p>

          <div className="space-y-2">
            <label className="text-xs text-[#999] font-medium">Reason</label>
            <div className="space-y-1.5">
              {REPORT_REASONS.map((r) => (
                <label
                  key={r.value}
                  className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                    reason === r.value
                      ? 'border-violet-500/50 bg-violet-500/10'
                      : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
                  }`}
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                    className="sr-only"
                  />
                  <div className={`size-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                    reason === r.value ? 'border-violet-500' : 'border-white/20'
                  }`}>
                    {reason === r.value && <div className="size-2 rounded-full bg-violet-500" />}
                  </div>
                  <span className="text-sm text-white">{r.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs text-[#999] font-medium">Additional details (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide any additional context..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.08] text-sm text-white placeholder-[#555] resize-none focus:outline-none focus:border-violet-500/50"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.05] text-sm text-[#888] hover:bg-white/[0.1] hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!reason || isSubmitting}
              className="flex-1 px-4 py-2.5 rounded-lg bg-red-500/80 text-sm text-white font-medium hover:bg-red-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <Flag className="size-4" />}
              Submit Report
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
